import { useState, useEffect } from "react"
import { BackButton } from "@/components/custom/BackButton"
import { PageTitle } from "@/components/custom/PageTitle"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { useLocation } from "react-router-dom"
import { UserRole, userRole } from "@/constants/users/userRole"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { useNavigate } from "react-router-dom"
import { User } from "@/types/users"
import { deleteUserFromOrganization, updateUserInOrganization } from "@/services/userService"
import { AlertDialogButton } from "@/components/custom/AlertDialogButton/AlertDialogButton"

import { Form } from "@/components/ui/form"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { getFormProps } from "@/helpers/formHelpers"
import { SelectInputField } from "@/components/custom/InputField/SelectInputField"
import { AlertDialogButtons, AlertDialogDescription, AlertDialogTitle } from "@/constants/alert-dialog/alert-dialog"
import { ButtonVariant } from "@/constants/button/button"

const formSchema = z.object({
  email: z.string().email(),
  role: z.string().min(3, {
    message: 'Please Select User Role.'
  })
})

export const EditUser = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token, users, userData } = useSelector((state: RootState) => state.user)
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: selectedUser?.email ?? "",
      role: selectedUser?.role ?? ""
    }
  })
  const formProps = getFormProps(form, formSchema)

  const onUpdateUser = async (
    values: z.infer<typeof formSchema>
  ) => {
    if (!token || !selectedUser?.id) return
    if (selectedUser.role !== form.getValues('role')) {
      const data = {
        role: values.role.toLowerCase()
      }
      const response = await dispatch(updateUserInOrganization(token, selectedUser.id, data))
      if (response.data) {
        navigate('/users')
      }
    }
  }

  const onDeleteUser = async () => {
    if (!token || !selectedUser?.id) return
    const response = await dispatch(deleteUserFromOrganization(
      token,
      selectedUser.id
    ))
    if (response.data) {
      navigate('/users')
    }
  }

  useEffect(() => {
    const userId = location.pathname.split('/users/edit/')[1]
    if (userId && users.length) {
      const user = users.find(u => u.id === userId)
      if (user) {
        setSelectedUser(user)
        form.setValue('email', user.email)
        form.setValue('role', user.role)
      } else {
        navigate('/users')
      }
    }
  }, [location.pathname, users, navigate, form])

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/users" buttonText="Back to Users" />
      <PageTitle title="Edit User" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onUpdateUser)}
          className="space-y-8 flex flex-col items-start"
        >
          <TextInputField
            name="email"
            label="Email Address"
            error={!!form.formState.errors.email}
            type="email"
            placeholder="john@example.com"
            isDisabled={true}
            required={true}
            {...formProps}
          />
          <SelectInputField
            name="role"
            label="Role"
            selectOptions={userRole}
            required={true}
            isDisabled={!userData?.isTCSAdmin}
            additionalClassName="capitalize"
            {...formProps}
          />
          {userData?.role !== UserRole.USER && (
            <div className="flex flex-row items-center justify-start">
              <Button
                type="submit"
                className="mb-2 font-semibold mr-6"
                disabled={selectedUser?.role.toLowerCase() === form.getValues('role').toLowerCase()}
              >
                Update User
              </Button>
              <AlertDialogButton
                actionButtonText={AlertDialogButtons.REMOVE}
                dialogTitle={AlertDialogTitle.REMOVE_USER}
                dialogSubtitle={AlertDialogDescription.REMOVE_USER}
                onClick={onDeleteUser}
                variant={ButtonVariant.LINK}
                actionButtonVariant={ButtonVariant.DESTRUCTIVE}
                isTableActionButton={false}
              />
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
